import { storeToRefs } from "pinia";

import { type UserLocation } from "shared/composables/useUserLocation";
import {
  CANADA,
  DEFAULT_LOCATION_IDS,
  EUROPE,
  EUROPE_EXCLUDED_LOCATION_IDS,
  NORTH_AMERICA,
  NORTH_AMERICA_LOCATION_IDS,
  PREFERRED_LOCATIONS_BY_REGION,
  SOUTH_AMERICA,
  TOP_LOCATION_NAMES,
  USA,
} from "shared/constants/locations";
import { useGlobalStore } from "shared/stores/global";
import { type Location } from "shared/types";
import { type Nullable } from "shared/types/utility";

export function getDefaultCountryLocations(region: string): string[] {
  if (NORTH_AMERICA_LOCATION_IDS.includes(region)) {
    return NORTH_AMERICA_LOCATION_IDS;
  }

  if (DEFAULT_LOCATION_IDS.includes(region)) {
    return [region];
  }

  return PREFERRED_LOCATIONS_BY_REGION[region] || [];
}

export function shouldIncludeLocation(
  location: Location,
  { continent, region }: { continent: string; region: string }
): boolean {
  const defaultCountryLocations = getDefaultCountryLocations(region);

  if (continent === EUROPE.name) {
    const excludedLocation = EUROPE_EXCLUDED_LOCATION_IDS.find(
      (id) => id === region
    );

    if (excludedLocation) {
      return location.country_code === excludedLocation;
    }

    return location.name === EUROPE.name;
  }

  return (
    defaultCountryLocations.includes(location.country_code ?? "") ||
    defaultCountryLocations.includes(location.name)
  );
}

export function getTopLocations(
  locations: Location[],
  userLocation: UserLocation
): Location[] {
  const globalStore = useGlobalStore();
  const { isStreem } = storeToRefs(globalStore);

  let topLocationNames = [...TOP_LOCATION_NAMES];

  // Workaround for AU environment until we have all required locations in the database
  if (isStreem.value) {
    // Filter out usa and canada
    topLocationNames = topLocationNames.filter(
      (location) => ![USA.name, CANADA.name].includes(location)
    );

    // Insert north america before south america
    const index = topLocationNames.indexOf(SOUTH_AMERICA.name);
    topLocationNames.splice(index, 0, NORTH_AMERICA.name);
  }

  const filteredLocations = locations.filter((location) =>
    topLocationNames.includes(location.name)
  );

  const { continent, region } = userLocation;

  let preferredLocations: string[] = [];

  if (region) {
    preferredLocations = PREFERRED_LOCATIONS_BY_REGION[region] || [];
  }

  if (preferredLocations.length === 0 && continent) {
    preferredLocations =
      PREFERRED_LOCATIONS_BY_REGION[continent.toUpperCase()] || [];
  }

  const locationOrder = [
    ...preferredLocations,
    ...topLocationNames.filter(
      (location) => !preferredLocations.includes(location)
    ),
  ];

  return filteredLocations.sort((locationA, locationB) => {
    const locationAIndex = locationOrder.indexOf(locationA.name);
    const locationBIndex = locationOrder.indexOf(locationB.name);

    if (locationAIndex === locationBIndex) {
      if (locationA.position && locationB.position) {
        return locationA.position - locationB.position;
      }
    }

    return locationAIndex - locationBIndex;
  });
}

export function formattedLocationHierarchy(locationNames: string[]): string {
  return [...locationNames]
    .filter((location) => location !== "Worldwide")
    .join(" > ");
}

export function locationLabel(location: {
  name: string;
  parent: Nullable<{ name: string }>;
  type: string;
}): string {
  const { name, parent, type } = location;

  if (parent && ["City", "State"].includes(type)) {
    return `${name}, ${parent.name} (${type})`;
  }

  return `${name} (${type})`;
}
